import { useState, useRef, useEffect, useMemo } from "react";
import { MessageCircle, X, Send, Loader2, User, Bot, Calendar, AlertTriangle, ChevronDown } from "lucide-react";
import axios from "axios";
import { Calendar as DateCalendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget({ slug, companyName, widgetColor = "#3B82F6", greeting, assistantName }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Extended widget config (booking engine, quick replies, emergency link)
  const [config, setConfig] = useState({ booking_enabled: false, quick_replies: [], emergency: { enabled: false, url: null }, max_advance_days: 30, available_days: {} });

  // Booking flow
  const [showBooking, setShowBooking] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [bookingSubmitted, setBookingSubmitted] = useState(false);
  const [bookingSending, setBookingSending] = useState(false);
  const [booking, setBooking] = useState({ name: "", phone: "", email: "", description: "", date: "" });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotMessage, setSlotMessage] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  const [showPeek, setShowPeek] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const peekStorageKey = `qs-chat-peek-shown:${slug || "default"}`;

  // Disable past, too-far-ahead, and non-working weekdays — matches the Quote Booking page.
  const disabledDays = useMemo(() => {
    const past = { before: new Date() };
    const maxD = new Date();
    maxD.setDate(maxD.getDate() + (config.max_advance_days || 30));
    const future = { after: maxD };
    const dayNameToNum = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
    const availDays = config.available_days;
    if (!availDays) return [past, future];
    const enabledDayNames = Array.isArray(availDays)
      ? availDays
      : Object.entries(availDays).filter(([, v]) => v && v.enabled).map(([k]) => k);
    if (enabledDayNames.length === 0) return [past, future];
    const availableDayNums = enabledDayNames.map(d => dayNameToNum[d]);
    const unavailableDayNums = [0, 1, 2, 3, 4, 5, 6].filter(d => !availableDayNums.includes(d));
    return [past, future, ...(unavailableDayNums.length > 0 ? [{ dayOfWeek: unavailableDayNums }] : [])];
  }, [config.available_days, config.max_advance_days]);

  const pickDate = (d) => {
    if (!d) return;
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setBooking(b => ({ ...b, date: dateStr }));
    setDatePickerOpen(false);
    loadSlots(dateStr);
  };

  // Fetch extended config (booking availability, quick replies, emergency link)
  useEffect(() => {
    if (!slug) return;
    axios.get(`${API_URL}/api/chat/public/embed/${slug}?check_my_page=true`)
      .then(res => setConfig(c => ({ ...c, ...res.data, emergency: res.data.emergency || { enabled: false, url: null } })))
      .catch(() => {});
  }, [slug]);

  // Greeting on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const fallbackName = assistantName || "Victoria";
      const greetText = greeting || `Hi! I'm ${fallbackName}, the virtual assistant for ${companyName || "this business"}. How can I help you today?`;
      setMessages([{ role: "assistant", content: greetText }]);
    }
  }, [isOpen]);

  // Idle peek
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { if (sessionStorage.getItem(peekStorageKey) === "1") return; } catch { /* ignore */ }
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const showTimer = setTimeout(() => setShowPeek(true), 4000);
    const hideTimer = setTimeout(() => setShowPeek(false), 16000);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, [peekStorageKey]);

  const dismissPeek = () => {
    setShowPeek(false);
    try { sessionStorage.setItem(peekStorageKey, "1"); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (isOpen && showPeek) dismissPeek();
    if (isOpen) { try { sessionStorage.setItem(peekStorageKey, "1"); } catch { /* ignore */ } }
  }, [isOpen]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, showBooking, slots]);

  useEffect(() => { if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 300); }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/chat/public/${slug}`, { message: userMsg, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.reply }]);
      const reply = res.data.reply.toLowerCase();
      if ((reply.includes("book") || reply.includes("quote visit") || reply.includes("details")) && !bookingSubmitted) {
        setShowBooking(true);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  // Quick-reply chip tapped → show label + canned answer locally (no API cost)
  const tapQuickReply = (qr) => {
    setMessages(prev => [...prev, { role: "user", content: qr.label }, { role: "assistant", content: qr.answer }]);
  };

  // Fetch free slots for a chosen date (real Quote Booking engine, GCal-checked)
  const loadSlots = async (date) => {
    setSelectedTime("");
    setSlots([]);
    setSlotMessage("");
    if (!date) return;
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API_URL}/api/chat/public/${slug}/availability?date=${date}`);
      const s = res.data.slots || [];
      setSlots(s);
      if (s.length === 0) setSlotMessage(res.data.message || "No free slots on that day — try another date.");
    } catch {
      setSlotMessage("Couldn't load availability. Please try another date.");
    } finally {
      setLoadingSlots(false);
    }
  };

  // REAL booking (blocks slot + GCal + creates customer/draft quote)
  const submitSlotBooking = async () => {
    if (!booking.name || !booking.phone || !booking.description || !booking.date || !selectedTime) return;
    setBookingSending(true);
    try {
      await axios.post(`${API_URL}/api/chat/public/${slug}/book-slot`, {
        session_id: sessionId,
        date: booking.date,
        time: selectedTime,
        customer_name: booking.name,
        customer_phone: booking.phone,
        customer_email: booking.email || "",
        job_description: booking.description,
      });
      setBookingSubmitted(true);
      setShowBooking(false);
      const dateLabel = new Date(booking.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      setMessages(prev => [...prev, { role: "assistant", content: `You're booked in, ${booking.name}! A quote visit is confirmed for ${dateLabel} at ${selectedTime}. ${companyName || "We"} will see you then.` }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMessages(prev => [...prev, { role: "assistant", content: detail === "This time slot has already been booked" ? "Sorry, that slot was just taken. Please pick another time." : "Sorry, there was an issue booking that slot. Please try again or call us directly." }]);
      if (detail === "This time slot has already been booked" && booking.date) loadSlots(booking.date);
    } finally {
      setBookingSending(false);
    }
  };

  // Fallback enquiry (no dates promised) when Quote Booking isn't set up
  const submitEnquiry = async () => {
    if (!booking.name || !booking.phone || !booking.description) return;
    setBookingSending(true);
    try {
      await axios.post(`${API_URL}/api/chat/public/${slug}/book`, {
        session_id: sessionId,
        customer_name: booking.name,
        customer_phone: booking.phone,
        description: booking.description,
      });
      setBookingSubmitted(true);
      setShowBooking(false);
      setMessages(prev => [...prev, { role: "assistant", content: `Thanks ${booking.name}! We've got your details and ${companyName || "the team"} will be in touch to arrange a quote visit.` }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, there was an issue sending your details. Please try calling us directly." }]);
    } finally {
      setBookingSending(false);
    }
  };

  const openEmergency = () => {
    if (config.emergency?.url) window.open(config.emergency.url, "_blank", "noopener");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const showQuickReplies = (config.quick_replies || []).length > 0 && !showBooking && messages.length <= 2;

  return (
    <>
      {!isOpen && showPeek && (
        <div className="fixed bottom-20 right-5 z-50 max-w-[260px] animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="chat-widget-peek">
          <button type="button" onClick={() => { dismissPeek(); setIsOpen(true); }} className="group relative block w-full text-left bg-white rounded-2xl rounded-br-sm shadow-xl border border-gray-100 px-4 py-3 pr-8 hover:shadow-2xl transition-shadow">
            <span className="block text-xs text-gray-500 mb-0.5">{assistantName || "Victoria"} · Virtual Assistant</span>
            <span className="block text-sm text-gray-900 leading-snug">👋 Hi! I'm {assistantName || "Victoria"} — ask me anything about {companyName || "us"}.</span>
            <span aria-hidden className="absolute -bottom-2 right-4 w-3 h-3 bg-white border-r border-b border-gray-100 transform rotate-45" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); dismissPeek(); }} className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors" aria-label="Dismiss" data-testid="chat-widget-peek-dismiss">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {!isOpen && (
        <button onClick={() => { dismissPeek(); setIsOpen(true); }} data-testid="chat-widget-toggle" className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg text-white transition-all duration-300 hover:scale-105 hover:shadow-xl" style={{ backgroundColor: widgetColor }}>
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">Chat with us</span>
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-5 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="chat-widget-window">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 text-white flex-shrink-0" style={{ backgroundColor: widgetColor }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Bot className="w-4 h-4" /></div>
              <div>
                <p className="text-sm font-semibold leading-tight">{companyName || "Chat"}</p>
                <p className="text-[10px] opacity-80">Virtual Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {config.emergency?.enabled && (
                <button onClick={openEmergency} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition" data-testid="chat-emergency-btn" title="Report an emergency">
                  <AlertTriangle className="w-3.5 h-3.5" /> Emergency
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition" data-testid="chat-widget-close"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: widgetColor + "20" }}><Bot className="w-3 h-3" style={{ color: widgetColor }} /></div>
                )}
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${msg.role === "user" ? "text-white rounded-br-sm" : "bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100"}`} style={msg.role === "user" ? { backgroundColor: widgetColor } : {}}>{msg.content}</div>
                {msg.role === "user" && (
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-1"><User className="w-3 h-3 text-gray-500" /></div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex gap-2 justify-start">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: widgetColor + "20" }}><Bot className="w-3 h-3" style={{ color: widgetColor }} /></div>
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Quick-reply chips */}
            {showQuickReplies && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-quick-replies">
                {config.quick_replies.map((qr, i) => (
                  <button key={i} onClick={() => tapQuickReply(qr)} className="px-3 py-1.5 text-xs font-medium rounded-full border transition hover:bg-gray-100" style={{ borderColor: widgetColor + "55", color: widgetColor }} data-testid={`chat-quick-reply-chip-${i}`}>
                    {qr.label}
                  </button>
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Booking panel */}
          {showBooking && !bookingSubmitted && (
            <div className="px-4 py-3 bg-blue-50 border-t border-blue-100 space-y-2 flex-shrink-0 max-h-[300px] overflow-y-auto" data-testid="chat-booking-panel">
              <div className="text-xs font-semibold text-blue-800 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {config.booking_enabled ? "Book a Quote Visit" : "Request a Quote Visit"}
              </div>
              <input type="text" placeholder="Your name" value={booking.name} onChange={e => setBooking(b => ({ ...b, name: e.target.value }))} className="w-full px-3 py-1.5 text-sm rounded-lg border border-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-400" data-testid="chat-booking-name" />
              <input type="tel" placeholder="Phone number" value={booking.phone} onChange={e => setBooking(b => ({ ...b, phone: e.target.value }))} className="w-full px-3 py-1.5 text-sm rounded-lg border border-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-400" data-testid="chat-booking-phone" />
              <input type="text" placeholder="What do you need? (brief description)" value={booking.description} onChange={e => setBooking(b => ({ ...b, description: e.target.value }))} className="w-full px-3 py-1.5 text-sm rounded-lg border border-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-400" data-testid="chat-booking-description" />

              {config.booking_enabled ? (
                <>
                  <input type="email" placeholder="Email (optional)" value={booking.email} onChange={e => setBooking(b => ({ ...b, email: e.target.value }))} className="w-full px-3 py-1.5 text-sm rounded-lg border border-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-400" data-testid="chat-booking-email" />
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" className="w-full flex items-center justify-between px-3 py-1.5 text-sm rounded-lg border border-blue-200 bg-white text-left hover:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400" data-testid="chat-booking-date">
                        <span className={booking.date ? "text-gray-800" : "text-gray-400"}>
                          {booking.date ? new Date(booking.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "Choose an available day"}
                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" data-testid="chat-booking-calendar-popover">
                      <DateCalendar
                        mode="single"
                        selected={booking.date ? new Date(booking.date + "T00:00:00") : undefined}
                        onSelect={pickDate}
                        disabled={disabledDays}
                        data-testid="chat-booking-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                  {loadingSlots && <p className="text-xs text-blue-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking free times…</p>}
                  {!loadingSlots && booking.date && slots.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="chat-booking-slots">
                      {slots.map((s) => (
                        <button key={s.time} onClick={() => setSelectedTime(s.time)} className={`px-2.5 py-1 text-xs rounded-lg border transition ${selectedTime === s.time ? "text-white border-transparent" : "bg-white text-gray-700 border-blue-200 hover:border-blue-400"}`} style={selectedTime === s.time ? { backgroundColor: widgetColor } : {}} data-testid={`chat-booking-slot-${s.time}`}>
                          {s.time}
                        </button>
                      ))}
                    </div>
                  )}
                  {!loadingSlots && booking.date && slots.length === 0 && slotMessage && (
                    <p className="text-xs text-gray-500" data-testid="chat-booking-no-slots">{slotMessage}</p>
                  )}
                  <button onClick={submitSlotBooking} disabled={!booking.name || !booking.phone || !booking.description || !booking.date || !selectedTime || bookingSending} className="w-full px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition" style={{ backgroundColor: widgetColor }} data-testid="chat-booking-submit">
                    {bookingSending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : selectedTime ? `Book ${selectedTime}` : "Pick a time"}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-[11px] text-gray-500">Leave your details and we'll be in touch to arrange a visit.</p>
                  <button onClick={submitEnquiry} disabled={!booking.name || !booking.phone || !booking.description || bookingSending} className="w-full px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition" style={{ backgroundColor: widgetColor }} data-testid="chat-booking-submit">
                    {bookingSending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Send my details"}
                  </button>
                </>
              )}
              <button onClick={() => setShowBooking(false)} className="text-xs text-blue-600 hover:underline">I'll just keep chatting</button>
            </div>
          )}

          {/* Input Bar */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-200 bg-white flex-shrink-0">
            <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type a message..." disabled={sending} className="flex-1 text-sm px-3 py-2 rounded-full bg-gray-100 border-0 focus:outline-none focus:ring-2 focus:ring-offset-0" style={{ "--tw-ring-color": widgetColor }} data-testid="chat-widget-input" />
            <button onClick={sendMessage} disabled={!input.trim() || sending} className="w-9 h-9 rounded-full flex items-center justify-center text-white disabled:opacity-40 transition hover:opacity-90" style={{ backgroundColor: widgetColor }} data-testid="chat-widget-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
